import { EHeadingStyleVariant, ELinkVariant, Heading, Link } from '@outdoorsyco/bonfire';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import ReactMarkdown from 'react-markdown';

import { ECancellationPolicies, POLICY_LABEL } from '@/constants/houseRules';

import ClampModal from '../../../common/ClampModal/ClampModal';
import css from './CancellationPolicy.module.css';

interface ICancellationPolicyProps {
  /**
   * Omitted by callers that have no rental-level policy to label — the
   * campground page reads the policy from a backing rental, so it renders the
   * static header like campsites do.
   */
  policy?: ECancellationPolicies;
  markdown?: string;
  isCampsite?: boolean;
}

const CancellationPolicy: React.FC<ICancellationPolicyProps> = ({
  policy,
  markdown,
  isCampsite,
}) => {
  const intl = useIntl();

  // Extract the first paragraph (before the first double line break) for the clamped preview
  const firstParagraph = markdown?.split('\n\n')[0] || '';

  const ModalContent = () => (
    <div className={css.policyContent} data-modal="true">
      <ReactMarkdown>{markdown || ''}</ReactMarkdown>
      <div className="mt-6">
        <Link
          variant={ELinkVariant.Underline}
          href="https://www.outdoorsy.com/help/what-is-the-cancellation-policy"
          target="_blank">
          <FormattedMessage
            id="v0D6ea"
            defaultMessage="More details"
            description="Listing > Cancellation Policy > Modal > More details link"
          />
        </Link>
      </div>
    </div>
  );

  return (
    <div>
      <Heading variant={EHeadingStyleVariant.H5}>
        {/* Campsites keep a static header — their policy type isn't meaningful to renters */}
        {isCampsite || !policy ? (
          <FormattedMessage defaultMessage="Cancellation policy" id="SYK3ab" />
        ) : (
          <FormattedMessage
            defaultMessage="Cancellation: {policy}"
            id="hzxJ0j"
            values={{ policy: POLICY_LABEL[policy] }}
          />
        )}
      </Heading>
      <div className="mt-6">
        {markdown && (
          <ClampModal
            ellipsis
            lineCount={10}
            hasMore
            modalTitle={intl.formatMessage({
              id: 'GamCVe',
              defaultMessage: 'Cancellation policy',
              description: 'Listing > Cancellation Policy > Modal title',
            })}
            modalContent={<ModalContent />}>
            <div className={css.policyContent}>
              <ReactMarkdown>{firstParagraph}</ReactMarkdown>
            </div>
          </ClampModal>
        )}
      </div>
    </div>
  );
};

export default React.memo(CancellationPolicy);
